import { useState } from "react";
import { Smartphone, Share, PlusSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { needsHomeScreen, isStandalone, sendTestPush, subscribePush, unsubscribePush, usePushStatus } from "@/lib/push";

const deviceName = (d) => {
  const ua = d.user_agent || "";
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android" : /Mac/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Device";
  const browser = /CriOS|Chrome/.test(ua) ? "Chrome" : /FxiOS|Firefox/.test(ua) ? "Firefox" : /Edg/.test(ua) ? "Edge" : /Safari/.test(ua) ? "Safari" : "browser";
  return `${os} · ${browser}`;
};

export const HomeScreenSteps = () => (
  <ol className="tc-homescreen-steps" data-testid="homescreen-steps">
    <li><Share size={14} /><span>Tap the <b>Share</b> button in Safari</span></li>
    <li><PlusSquare size={14} /><span>Choose <b>Add to Home Screen</b></span></li>
    <li><Smartphone size={14} /><span>Open <b>Total Chat</b> from your Home Screen and turn on notifications</span></li>
  </ol>
);

export const usePushToggle = () => {
  const [busy, setBusy] = useState(false);
  const enable = async () => {
    setBusy(true);
    try {
      const out = await subscribePush();
      toast.success(out.devices > 1 ? `Push notifications are on (${out.devices} devices)` : "Push notifications are on for this device");
      return true;
    } catch (e) {
      const m = e?.message;
      if (m === "homescreen") toast.info("On iPhone, add Total Chat to your Home Screen first");
      else if (m === "denied") toast.error("Notifications are blocked in your browser settings for this site");
      else if (m === "disabled") toast.error("Push notifications aren't configured on the server yet");
      else if (m === "timeout") toast.error("That took too long — check your browser's notification settings and try again");
      else if (m === "unsupported") toast.error("This browser can't receive push notifications");
      else if (m !== "default") toast.error(errMsg(e, "Couldn't turn on push notifications"));
      return false;
    } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await unsubscribePush(); toast.success("Push notifications are off on this device"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return { busy, enable, disable };
};

export const PushStatus = ({ prefs, update }) => {
  const st = usePushStatus();
  const { busy, enable, disable } = usePushToggle();
  const test = async () => {
    try {
      const r = await sendTestPush();
      toast.success(r.sent ? `Test sent to ${r.sent} device${r.sent === 1 ? "" : "s"} — check your lock screen` : "No device received the test");
    } catch (e) { toast.error(errMsg(e, "Couldn't send a test push")); }
  };
  const homescreen = needsHomeScreen();
  const label = !st.supported ? <span className="tc-pill">Not supported here</span> : st.enabled === false ? <span className="tc-pill">Not configured</span>
    : st.subscribed ? <span className="tc-pill server">On · this device</span> : st.permission === "denied" ? <span className="tc-pill danger">Blocked</span> : <span className="tc-pill">Off</span>;
  const desc = !st.supported ? "This browser can't receive push notifications. On iPhone use Safari 16.4+; on Android use Chrome."
    : homescreen ? "On iPhone and iPad, notifications only work after you add Total Chat to your Home Screen."
    : st.subscribed ? `Mentions, DMs${prefs.push_huddles !== false ? ", huddle invites" : ""} and the activity you pick below reach this ${isStandalone() ? "app" : "browser"} even when Total Chat is closed. Alerts pause while you're active on a desktop.`
    : st.permission === "denied" ? "Notifications are blocked for this site. Allow them in your browser or phone settings, then come back here."
    : "Get alerts on your lock screen when Total Chat is closed — on your phone or this computer.";
  return (
    <div data-testid="push-section">
      <div className="tc-notif-status" data-testid="push-status">
        <div className="tc-notif-status__icon"><Smartphone size={18} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4>Push notifications {label}</h4>
          <p>{desc}</p>
          {homescreen && <HomeScreenSteps />}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {st.supported && !homescreen && st.enabled !== false && !st.subscribed && st.permission !== "denied" && <button className="tc-btn tc-btn--primary sm" onClick={enable} disabled={busy} data-testid="push-enable">Turn on</button>}
          {st.subscribed && <button className="tc-btn tc-btn--outline sm" onClick={test} data-testid="push-test">Send a test</button>}
          {st.subscribed && <button className="tc-btn tc-btn--ghost sm" onClick={disable} disabled={busy} data-testid="push-disable">Turn off</button>}
        </div>
      </div>
      {st.devices?.length > 0 && (
        <div className="tc-push-devices" data-testid="push-devices">
          <span className="muted" style={{ fontSize: 12 }}>{st.devices.length} device{st.devices.length === 1 ? "" : "s"} receiving push</span>
          {st.devices.map((d) => (
            <span key={d.id} className="tc-chip" data-testid={`push-device-${d.id}`} title={d.user_agent}>{deviceName(d)}{d.standalone ? " (app)" : ""}
              <button title="Remove this device" onClick={async () => { try { await api.delete("/push/subscribe", { data: { endpoint: d.endpoint } }); analytics.track("push_device_removed", { category: "notifications" }); window.dispatchEvent(new Event("tc:push-changed")); } catch (e) { toast.error(errMsg(e)); } }} data-testid={`push-device-remove-${d.id}`}><Trash2 size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {(st.subscribed || st.devices?.length > 0) && (
        <label className="tc-switch-row" style={{ cursor: "pointer" }}>
          <div><h4>Huddle invites</h4><p>Push a notification when someone starts a huddle in a DM with you, so you can tap to join.</p></div>
          <Switch checked={prefs.push_huddles !== false} onCheckedChange={(v) => update({ push_huddles: v })} data-testid="pref-push-huddles" />
        </label>
      )}
    </div>
  );
};
